import { Injectable } from '@angular/core';
import { SESSION_STORAGE_KEY } from '@core/configs';
import { AvatarBatchResult, AvatarOptimizationEvent, AvatarProcessingProgress } from '@core/models/avatar-process.model';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AvatarOptimizationService } from './avatar-process.service';
import { SessionStorageService } from './session-storage.service';

/**
 * Per-daicd avatar lifecycle:
 * - loading  : requested, not yet resolved → UI shows default + spinner
 * - resolved : has an avatar image → UI shows the image
 * - empty    : stable "no source image" → UI shows default, no spinner, not re-requested
 * - failed   : transient failure (connection drop / batch error) → UI shows default,
 *              no spinner, but MAY be re-requested later (not blacklisted)
 * - idle     : never requested in this session
 */
export type AvatarStatus = 'idle' | 'loading' | 'resolved' | 'empty' | 'failed';

interface AvatarRequestState {
  pending: Set<string>;
}

@Injectable({ providedIn: 'root' })
export class AvatarDealerService {
  private readonly avatarSources = new Map<string, string>();
  private readonly avatarSubjects = new Map<string, BehaviorSubject<string | undefined>>();
  private readonly statusSubjects = new Map<string, BehaviorSubject<AvatarStatus>>();
  private readonly inFlightDaiCds = new Set<string>();
  // Stable "no source image" — excluded from re-request. Transient failures are
  // intentionally NOT tracked here so a dropped connection can be retried.
  private readonly emptyDaiCds = new Set<string>();
  // daicds already re-fetched once after a broken image URL — guards against a
  // retry loop when the regenerated URL is still unloadable.
  private readonly urlRetriedDaiCds = new Set<string>();
  // daicds whose self-heal re-fetch is in flight (awaiting a resolved/empty
  // outcome), plus running totals for observability.
  private readonly pendingRetry = new Set<string>();
  private deadAvatarCount = 0;
  private healedAvatarCount = 0;
  private readonly callerRequests = new Map<string, AvatarRequestState>();
  private readonly callerCompletedSubject = new Subject<string>();
  private readonly pendingSessionUpdates = new Map<string, string>();
  private isProcessing = false;
  private sessionFlushTimer?: ReturnType<typeof setTimeout>;
  private queue = new Set<string>();

  // Socket lifecycle: the dealer owns one persistent socket. It opens lazily on
  // the first request (inside the optimization service) and is closed after
  // this idle window with no in-flight or queued work; a new request reopens it.
  private static readonly SOCKET_IDLE_MS = 10000;
  private idleCloseTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly avatarOptimizationService: AvatarOptimizationService,
    private readonly sessionStorageService: SessionStorageService
  ) {
    this.hydrateFromSession();
  }

  requestAvatars(daiCds: string[], callerId: string = this.createCallerId()): string {
    const pendingDaiCds = this.getPendingDaiCds(daiCds);
    console.log(`[AvatarDealer] requestAvatars requested=${(daiCds || []).length} pending=${pendingDaiCds.length}`);
    if (pendingDaiCds.length === 0) {
      setTimeout(() => this.callerCompletedSubject.next(callerId), 0);
      return callerId;
    }

    this.cancelIdleClose();
    this.callerRequests.set(callerId, { pending: new Set(pendingDaiCds) });
    pendingDaiCds.forEach(daicd => {
      this.setStatus(daicd, 'loading');
      if (!this.inFlightDaiCds.has(daicd) && !this.queue.has(daicd)) {
        this.queue.add(daicd);
      }
    });
    this.processQueue();
    return callerId;
  }

  avatarUrl$(daicd: string): Observable<string | undefined> {
    return this.getSubject(daicd).asObservable();
  }

  /**
   * Called by a render component when a resolved avatar image fails to load
   * (e.g. its S3 object was removed out-of-band). Since the dealer is an
   * app-lifetime singleton, a stale cached URL would otherwise never be
   * re-fetched until the tab is closed. Invalidate the cached URL and re-request
   * it — but only once per daicd per app session, so a persistently unloadable
   * URL cannot cause a retry loop.
   */
  retryFailedUrl(daicd: string): void {
    if (!daicd || this.urlRetriedDaiCds.has(daicd)) return;
    this.urlRetriedDaiCds.add(daicd);
    this.pendingRetry.add(daicd);
    this.deadAvatarCount++;
    this.avatarSources.delete(daicd);
    this.emptyDaiCds.delete(daicd);
    console.warn(`[AvatarDealer] broken avatar daicd=${daicd} — re-fetching (dead=${this.deadAvatarCount} healed=${this.healedAvatarCount} inFlight=${this.pendingRetry.size})`);
    this.requestAvatars([daicd]);
  }

  /** Per-daicd terminal status stream. See AvatarStatus. */
  status$(daicd: string): Observable<AvatarStatus> {
    return this.getStatusSubject(daicd).asObservable();
  }

  callerCompleted$(callerId: string): Observable<string> {
    return this.callerCompletedSubject.asObservable().pipe(
      filter(id => id === callerId),
      take(1)
    );
  }

  getAvatarUrl(daicd: string | undefined): string | undefined {
    if (!daicd) return undefined;
    const source = this.avatarSources.get(daicd);
    return source ? this.toFullAvatarUrl(source) : undefined;
  }

  /** Raw avatar source (S3 path), not the full URL. Callers that store the
   *  path and prepend the CDN base themselves (e.g. bathe records) use this. */
  getAvatarSource(daicd: string | undefined): string | undefined {
    if (!daicd) return undefined;
    return this.avatarSources.get(daicd);
  }

  clear(): void {
    this.avatarSources.clear();
    this.inFlightDaiCds.clear();
    this.emptyDaiCds.clear();
    this.urlRetriedDaiCds.clear();
    this.pendingRetry.clear();
    this.deadAvatarCount = 0;
    this.healedAvatarCount = 0;
    this.callerRequests.clear();
    this.queue.clear();
    this.pendingSessionUpdates.clear();
    if (this.sessionFlushTimer) clearTimeout(this.sessionFlushTimer);
    this.sessionFlushTimer = undefined;
    this.cancelIdleClose();
    this.isProcessing = false;
    this.avatarSubjects.forEach(subject => subject.next(undefined));
    this.statusSubjects.forEach(subject => subject.next('idle'));
  }

  private processQueue(): void {
    if (this.isProcessing) return;
    if (this.queue.size === 0) {
      this.scheduleIdleClose();
      return;
    }

    const daiCds = Array.from(this.queue);
    this.queue.clear();
    daiCds.forEach(daicd => this.inFlightDaiCds.add(daicd));
    this.isProcessing = true;

    const channelId = this.createCallerId();
    console.log(`[AvatarDealer] dispatch batch channel=${channelId} size=${daiCds.length}`);
    this.avatarOptimizationService.processingAvatars(daiCds, channelId).subscribe({
      next: event => this.handleAvatarEvent(event),
      error: () => {
        // Transient failure for the whole batch (connection drop / ProcessingError):
        // do NOT blacklist — leave these re-requestable. The cause is logged by
        // avatar-process (ProcessingError / Failed to start batch).
        daiCds.forEach(daicd => this.markTransientFailed(daicd));
        this.isProcessing = false;
        this.processQueue();
      },
      complete: () => {
        // Any daicd not resolved by now had no source image → stable empty.
        daiCds.forEach(daicd => this.finalizeDaiCd(daicd));
        this.isProcessing = false;
        this.processQueue();
      }
    });
  }

  private handleAvatarEvent(event: AvatarOptimizationEvent): void {
    if (event.type === 'progress') {
      const progress = event.data as AvatarProcessingProgress;
      const imageResult = progress.imageResult;
      if (imageResult?.daiCd && imageResult.cdnUrl && imageResult.status === 'success') {
        this.setAvatarSource(imageResult.daiCd, imageResult.cdnUrl);
        this.resolvePending(imageResult.daiCd);
      }
      return;
    }

    if (event.type === 'complete') {
      const batchResult = event.data as AvatarBatchResult;
      (batchResult.results || []).forEach(result => {
        if (!result.daiCd) return;
        if (result.cdnUrl && result.status !== 'failed') {
          this.setAvatarSource(result.daiCd, result.cdnUrl);
        } else {
          this.markEmpty(result.daiCd);
        }
        this.resolvePending(result.daiCd);
      });
    }
  }

  private setAvatarSource(daicd: string, source: string): void {
    this.avatarSources.set(daicd, source);
    this.emptyDaiCds.delete(daicd);
    this.inFlightDaiCds.delete(daicd);
    this.getSubject(daicd).next(this.toFullAvatarUrl(source));
    this.setStatus(daicd, 'resolved');
    this.scheduleSessionMerge(daicd, source);
    if (this.pendingRetry.delete(daicd)) {
      this.healedAvatarCount++;
      console.log(`[AvatarDealer] self-healed daicd=${daicd} (healed=${this.healedAvatarCount}/${this.deadAvatarCount} inFlight=${this.pendingRetry.size})`);
    }
  }

  /** Stable "no image" outcome — not re-requested. */
  private markEmpty(daicd: string): void {
    this.inFlightDaiCds.delete(daicd);
    if (this.avatarSources.has(daicd)) return; // resolved wins
    this.emptyDaiCds.add(daicd);
    this.setStatus(daicd, 'empty');
    this.reportRetryFailed(daicd, 'empty');
  }

  /** Transient failure — spinner stops, but daicd stays re-requestable. */
  private markTransientFailed(daicd: string): void {
    this.inFlightDaiCds.delete(daicd);
    if (this.avatarSources.has(daicd)) {
      this.resolvePending(daicd);
      return;
    }
    this.setStatus(daicd, 'failed');
    this.resolvePending(daicd);
    this.reportRetryFailed(daicd, 'transient');
  }

  /** A self-heal re-fetch ended without a usable image. */
  private reportRetryFailed(daicd: string, reason: 'empty' | 'transient'): void {
    if (this.pendingRetry.delete(daicd)) {
      console.warn(`[AvatarDealer] self-heal FAILED daicd=${daicd} reason=${reason} — kept default (healed=${this.healedAvatarCount}/${this.deadAvatarCount} inFlight=${this.pendingRetry.size})`);
    }
  }

  /** Give every batch daicd a terminal status on normal completion. */
  private finalizeDaiCd(daicd: string): void {
    this.inFlightDaiCds.delete(daicd);
    if (!this.avatarSources.has(daicd)) {
      this.markEmpty(daicd);
    }
    this.resolvePending(daicd);
  }

  private resolvePending(daicd: string): void {
    this.callerRequests.forEach((state, callerId) => {
      state.pending.delete(daicd);
      if (state.pending.size === 0) {
        this.callerRequests.delete(callerId);
        this.callerCompletedSubject.next(callerId);
      }
    });
  }

  private getPendingDaiCds(daiCds: string[]): string[] {
    return Array.from(new Set((daiCds || []).filter(Boolean))).filter(daicd => {
      return !this.avatarSources.has(daicd)
        && !this.emptyDaiCds.has(daicd);
    });
  }

  private getSubject(daicd: string): BehaviorSubject<string | undefined> {
    if (!this.avatarSubjects.has(daicd)) {
      this.avatarSubjects.set(daicd, new BehaviorSubject<string | undefined>(this.getAvatarUrl(daicd)));
    }
    return this.avatarSubjects.get(daicd)!;
  }

  private getStatusSubject(daicd: string): BehaviorSubject<AvatarStatus> {
    if (!this.statusSubjects.has(daicd)) {
      const initial: AvatarStatus = this.avatarSources.has(daicd)
        ? 'resolved'
        : (this.emptyDaiCds.has(daicd) ? 'empty' : 'idle');
      this.statusSubjects.set(daicd, new BehaviorSubject<AvatarStatus>(initial));
    }
    return this.statusSubjects.get(daicd)!;
  }

  private setStatus(daicd: string, status: AvatarStatus): void {
    const subject = this.getStatusSubject(daicd);
    if (subject.value !== status) subject.next(status);
  }

  private scheduleIdleClose(): void {
    if (this.idleCloseTimer) return;
    this.idleCloseTimer = setTimeout(() => {
      this.idleCloseTimer = undefined;
      // Only close if still idle (no work arrived while waiting).
      if (!this.isProcessing && this.queue.size === 0) {
        console.log('[AvatarDealer] idle — closing socket');
        this.avatarOptimizationService.closeConnection();
      }
    }, AvatarDealerService.SOCKET_IDLE_MS);
  }

  private cancelIdleClose(): void {
    if (this.idleCloseTimer) {
      clearTimeout(this.idleCloseTimer);
      this.idleCloseTimer = undefined;
    }
  }

  private hydrateFromSession(): void {
    const daiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
    if (!Array.isArray(daiInfo)) return;

    daiInfo.forEach((item: any) => {
      if (item?.daicd && item?.avatarSource) {
        this.avatarSources.set(item.daicd, item.avatarSource);
      }
    });
  }

  private scheduleSessionMerge(daicd: string, source: string): void {
    this.pendingSessionUpdates.set(daicd, source);
    if (this.sessionFlushTimer) return;

    this.sessionFlushTimer = setTimeout(() => {
      this.sessionFlushTimer = undefined;
      this.flushSessionUpdates();
    }, 250);
  }

  private flushSessionUpdates(): void {
    if (this.pendingSessionUpdates.size === 0) return;

    const daiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
    if (!Array.isArray(daiInfo)) {
      this.pendingSessionUpdates.clear();
      return;
    }

    const updateMap = new Map(this.pendingSessionUpdates);
    this.pendingSessionUpdates.clear();
    let hasChange = false;

    daiInfo.forEach((item: any) => {
      const source = updateMap.get(item?.daicd);
      if (!source) return;

      item.avatarSource = source;
      item.face_img = this.toFullAvatarUrl(source);
      hasChange = true;
    });

    if (hasChange) this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.daiInfo, daiInfo);
  }

  private toFullAvatarUrl(source: string): string {
    if (!source) return '';
    if (/^https?:\/\//.test(source)) return source;
    return `${environment.avatarUrl}/${source}`;
  }

  private createCallerId(): string {
    return `avatar-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
